import api from "@/api";
import translateModel from "@/tradutors/model";
import { textoDoHtml } from "@/utils/descricaoDaPagina";
import { abasDoEnsaio } from "@/utils/modelTabs";
import {
  EMPTY_SCHEMA,
  ROTA_DA_COLUNA,
  ROTA_DO_POST,
  absoluteUrl,
  colunaDeVerdade,
  defineTool,
  jsonResult,
  postDeVerdade,
  textoLimitado,
  urlDaColuna,
  urlDoPost,
  type WebMcpToolsContext,
} from "./shared";

/**
 * Tools de conteúdo: ler um artigo, conhecer uma modelo e ver as novidades.
 * Registradas globalmente, como as de catálogo: valem em qualquer página e só
 * leem — a API e os textos são os mesmos que as páginas já mostram.
 *
 * Mesma regra de mídia do catálogo: nada de capa, foto, prévia ou vídeo, só
 * texto e endereço de página.
 */

/** Teto de caracteres por texto devolvido: cada um ocupa contexto do agente. */
const LIMITE_DO_ARTIGO = 8000;
const LIMITE_DA_ENTREVISTA = 4000;
const LIMITE_DA_APRESENTACAO = 1200;

/** Caminho de um endereço absoluto ou relativo, de qualquer domínio do site. */
function caminhoDe(endereco: string): string {
  try {
    return new URL(endereco, window.location.origin).pathname;
  } catch {
    return endereco;
  }
}

/** Idioma de um parâmetro de rota, quando é um dos dois do site. */
function idiomaDe(parametro: unknown): "pt-br" | "en" | null {
  return parametro === "pt-br" || parametro === "en" ? parametro : null;
}

/** Lista "últimos" da home e das listagens, já com o endereço de cada item. */
function ultimos(resposta: PromiseSettledResult<any>, montar: (item: any) => any) {
  if (resposta.status !== "fulfilled") return [];
  const itens = resposta.value?.data?.postsCarroselModels ?? [];
  return itens.filter((item: any) => item?.slug).map(montar);
}

export function contentTools(ctx: WebMcpToolsContext): WebMcpToolDescriptor[] {
  const { router, language, t } = ctx;
  const text = t.tools;
  const navBar =
    language === "en" ? translateModel.ingles.navBar : translateModel.portugues.navBar;

  return [
    defineTool(t, {
      name: "read_article",
      description: text.read_article.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          url: { type: "string", description: text.read_article.params.url },
        },
      },
      execute: async ({ url }, { signal }) => {
        // Sem endereço, vale o artigo aberto agora ("resume esta coluna").
        const rota = url
          ? router.resolve(caminhoDe(String(url)))
          : router.currentRoute.value;
        // O idioma do endereço manda: um link /en/ lê o artigo em inglês.
        const idioma = idiomaDe(rota.params.language) ?? language;

        let artigo: any = null;
        let tipo: "blog" | "column";
        if (rota.name === ROTA_DO_POST) {
          tipo = "blog";
          const { data } = await api.get(
            `v1/blog/getBlogId/${encodeURIComponent(String(rota.params.id))}?language=${idioma}`,
            { signal }
          );
          artigo = data?.blog?.[0];
          if (!postDeVerdade(artigo)) return t.messages.articleNotFound;
        } else if (rota.name === ROTA_DA_COLUNA) {
          tipo = "column";
          const { data } = await api.get(
            `v1/colunas/getColumnId/${encodeURIComponent(String(rota.params.colunaId))}?language=${idioma}`,
            { signal }
          );
          artigo = data?.postsColumns?.[0];
          if (!colunaDeVerdade(artigo)) return t.messages.articleNotFound;
        } else {
          return t.messages.notAnArticle;
        }

        const { texto, cortado } = textoLimitado(
          textoDoHtml(artigo.content),
          LIMITE_DO_ARTIGO
        );
        return jsonResult({
          type: tipo,
          title: artigo.title ?? "",
          date: artigo.date ?? null,
          author: artigo.author_name ?? null,
          category: artigo.category ?? null,
          url:
            tipo === "blog"
              ? urlDoPost(router, idioma, artigo)
              : urlDaColuna(router, idioma, artigo),
          text: texto,
          ...(cortado ? { note: t.messages.textTruncated } : {}),
        });
      },
    }),

    defineTool(t, {
      name: "get_model_details",
      description: text.get_model_details.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          model_slug: {
            type: "string",
            description: text.get_model_details.params.model_slug,
          },
        },
        required: ["model_slug"],
      },
      execute: async ({ model_slug }, { signal }) => {
        const slug = String(model_slug ?? "").trim();
        if (!slug) return t.messages.modelNotFound;
        const caminho = encodeURIComponent(slug);

        // Os mesmos dois endpoints das abas do ensaio: os metadados
        // (composables/useEnsaioSSR.ts) e o da entrevista, wallpapers e
        // créditos, que também diz quais abas a modelo tem.
        const [metadados, perfil] = await Promise.allSettled([
          api.get(`v3/modelos/${caminho}?language=${language}`, { signal }),
          api.get(`v2/modelos/${caminho}/perfilWallpaperCredits?language=${language}`, {
            signal,
          }),
        ]);

        // Slug que não existe volta como página de erro (HTML) ou exceção.
        const modelo =
          metadados.status === "fulfilled" ? metadados.value.data : null;
        if (!modelo?.nameModel) return t.messages.modelNotFound;

        // Falha no perfil não derruba o resto: sem ele, a resposta fica sem
        // entrevista e créditos, e só com as abas que toda modelo tem.
        const extras =
          perfil.status === "fulfilled" && !perfil.value.data?.message
            ? perfil.value.data
            : {};

        const entrevista = textoLimitado(
          textoDoHtml(extras.profileQuestion),
          LIMITE_DA_ENTREVISTA
        );

        return jsonResult({
          name: modelo.nameModel,
          slug,
          tagline: modelo.descriptionModel ?? null,
          about:
            textoLimitado(textoDoHtml(modelo.text), LIMITE_DA_APRESENTACAO).texto ||
            null,
          publishedOn: modelo.schedule ?? null,
          sections: abasDoEnsaio(navBar, extras).map((aba) => ({
            section: aba.path,
            name: aba.name,
            url: absoluteUrl(router, {
              name: `language-modelos-name-${aba.path}`,
              params: { language, name: slug },
            }),
          })),
          interview: entrevista.texto || null,
          credits: (extras.credits ?? []).map((credito: any) => ({
            role: credito.functions,
            people: credito.persons,
          })),
        });
      },
    }),

    defineTool(t, {
      name: "get_whats_new",
      description: text.get_whats_new.description,
      annotations: { readOnlyHint: true },
      inputSchema: EMPTY_SCHEMA,
      execute: async (_args, { signal }) => {
        // As mesmas fontes da home e das listagens. Cada uma pode falhar
        // sozinha sem apagar as outras.
        const [destaque, ensaios, makingOf, lives, blog, colunas] =
          await Promise.allSettled([
            api.get(`v1/home/getEssayPrimary?language=${language}&mobile=false`, {
              signal,
            }),
            api.get(`v1/modelos/getLastFiveModels?language=${language}`, { signal }),
            api.get(`v1/modelos/getMakingOfModelsLastFive?language=${language}`, {
              signal,
            }),
            api.get(`v1/modelos/getLiveModelsLastFive?language=${language}`, {
              signal,
            }),
            api.get(
              `v1/blog/getBlogs?search=&language=${language}&limit=6&mobile=false`,
              { signal }
            ),
            api.get(
              `v1/colunas/getColumns?search=&articletag=&language=${language}&limit=6&mobile=false`,
              { signal }
            ),
          ]);

        const paginaDaModelo = (rota: string) => (item: any) => ({
          name: item.title ?? "",
          url: absoluteUrl(router, {
            name: rota,
            params: { language, name: item.slug },
          }),
        });

        const semana =
          destaque.status === "fulfilled"
            ? destaque.value.data?.cardPincipal
            : null;

        return jsonResult({
          modelOfTheWeek: semana?.url
            ? {
                name: semana.name ?? "",
                tagline: semana.description ?? null,
                url: absoluteUrl(router, {
                  name: "language-modelos-name-photo",
                  params: { language, name: semana.url },
                }),
              }
            : null,
          latestPhotoShoots: ultimos(
            ensaios,
            paginaDaModelo("language-modelos-name-photo")
          ),
          latestMakingOf: ultimos(
            makingOf,
            paginaDaModelo("language-modelos-name-making-of")
          ),
          latestLives: ultimos(lives, paginaDaModelo("language-modelos-name-live")),
          latestBlogPosts:
            blog.status === "fulfilled"
              ? (blog.value.data?.blogs ?? [])
                  .filter(postDeVerdade)
                  .slice(0, 5)
                  .map((post: any) => ({
                    title: post.title ?? "",
                    date: post.date ?? null,
                    url: urlDoPost(router, language, post),
                  }))
              : [],
          latestColumns:
            colunas.status === "fulfilled"
              ? (colunas.value.data?.postsColumns ?? [])
                  .filter(colunaDeVerdade)
                  .slice(0, 5)
                  .map((coluna: any) => ({
                    title: coluna.title ?? "",
                    date: coluna.date ?? null,
                    url: urlDaColuna(router, language, coluna),
                  }))
              : [],
        });
      },
    }),
  ];
}
